import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';

import { Location, Unit } from '@core/booking/booking.types';
import { useSettingsStore } from '@core/settings/store/settings';

import { CityGroup } from './meeting-room-landing.types';
import MeetingRoomCard from './MeetingRoomCard';

interface RoomCardGridProps {
  cityGroups: CityGroup[];
  onRoomSelect: (room: Unit, location: Location) => void;
}

const RoomCardGrid: FC<RoomCardGridProps> = ({ cityGroups, onRoomSelect }) => {
  const { t } = useTranslation();
  const { textColor } = useSettingsStore();
  const [collapsedCities, setCollapsedCities] = useState<Record<string, boolean>>({});

  const toggleCity = useCallback((city: string) => {
    setCollapsedCities((prev) => ({ ...prev, [city]: !prev[city] }));
  }, []);

  if (cityGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-lg" style={{ color: textColor, opacity: 0.6 }}>
          {t('landing.noRoomsAvailable')}
        </p>
      </div>
    );
  }

  return (
    <div className="py-6">
      {cityGroups.map((group) => {
        const isCollapsed = !!collapsedCities[group.city];
        const roomCount = group.locations.reduce((sum, loc) => sum + loc.rooms.length, 0);

        return (
          <div key={group.city} className="mb-6">
            <button
              onClick={() => toggleCity(group.city)}
              className="flex items-center justify-between w-full bg-transparent border-none cursor-pointer px-0 py-2 mb-2"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold m-0" style={{ color: textColor }}>
                  {group.city === 'Other' ? t('landing.otherCity') : group.city}
                </h2>
                <span className="text-sm font-normal" style={{ color: textColor, opacity: 0.5 }}>
                  {t('landing.roomCount', { count: roomCount })}
                </span>
              </div>
              {isCollapsed ? (
                <IoChevronDown size={20} style={{ color: textColor }} />
              ) : (
                <IoChevronUp size={20} style={{ color: textColor }} />
              )}
            </button>

            {!isCollapsed && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {group.locations.flatMap((loc) =>
                  loc.rooms.map((room) => (
                    <MeetingRoomCard
                      key={room.id}
                      room={room}
                      locationName={loc.location.name}
                      locationAddress={loc.location.address}
                      onClick={() => onRoomSelect(room, loc.location)}
                    />
                  )),
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RoomCardGrid;
